import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { sortBy, uniqueId } from 'lodash'
import React, { FC, useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useHistory, useLocation, useParams } from 'react-router-dom'
import { RootState } from '../../../../states/containers/store'
import {
  fetchTaskReportLabelGroupDetail,
  fetchTaskReportLabelGroupDetailMax,
  fetchTaskReportLabelGroupDetailSummary,
  fetchTaskSearch,
  resetTaskReportLabelGroupDetailSummary,
  updateTaskReportLabelGroupDetailSummary,
} from '../../../../states/containers/task/taskSlice'
import { LangValue } from '../../../../states/types/common'
import { RequestGetTaskSearch, RequestPatchTaskReportLabelGroupDetailSummary } from '../../../../states/types/task'
import {
  dateManipulateMonth,
  diffMonth,
  endOfMonth,
  format,
  nowDate,
  secToHour,
  secToTwoDigitsMinute,
  startOfMonth,
} from '../../../../utils/date'
import { getGetPrams, setPath } from '../../../../utils/url'
import LoadingSquare from '../../../components/common/LoadingSquare'
import Tips from '../../../components/common/Tips'
import DetailCostChart from '../../../components/task/DetailCostChart'
import DetailScheduleChart from '../../../components/task/DetailScheduleChart'
import DetailCostInputArea from '../../../components/task/DetailCostInputArea'
import DetailScheduleInputArea from '../../../components/task/DetailScheduleInputArea'
import DetailRow from '../../../components/task/DetailRow'
import DateModule from '../../../components/task/MonthCalendarModule'
import { URL } from '../../../constants/url'
import Modal from '../../../components/common/Modal'
import { IconName, IconPrefix } from '@fortawesome/fontawesome-common-types'
import ReportSummaryModule from '../../../components/task/ReportSummaryModule'
import PdfModule from '../../../components/common/PdfModule'
import ExtraOrderModal from '../../../components/common/ExtraOrderModal'
import { isAdministrator } from '../../../../utils/role'
import { stringToHexSimple } from '../../../../utils/color'

interface GetParams {
  date: string
}

interface GraphData {
  date: string
  labeling: {
    labels: {
      id: number
      name: string
      color: string
      time: number
    }[]
    scheduled: number
  }
  accounting: {
    budget: number
    other_costs: {
        name: string
        cost: number
    }[]
    cost: number
  }
}

interface List {
  id: number
  name: string
  color: string
  schedule: {
    start_date: string // 開始日
    scheduled_end_date: string | null // 完了予定日
    actual_end_date: string | null // 完了日(nullの場合は未完了プロジェクト)
  }
  time: {
    scheduled: number | null // 予定時間
    actual: number // 実際にかかった時間
  }
  cost: {
    scheduled: number | null // 予算
    actual: number // 実際にかかったコスト
    others: { // その他コスト
      name: string
      cost: number
    }[]
  }
  labels: {
    id: number
    name: string
    time: number
    cost: number
    color: string
    members: {
      id: number
      name: string | null
      profile_image: string | null
      time: number
      cost: number
      approved: boolean
    }[]
    approved: boolean
  }[]
  label_codes: {
    code: string | null
    cost: number
    time: number
    labels: {
      id: number
      name: string
      code: string | null
      color: string
      cost: number
      time: number
      approved: boolean
    }[]
    approved: boolean
  }[]
  members: {
    id: number
    name: string | null
    profile_image: string | null
    cost: number
    time: number
    labels: {
      id: number
      name: string
      time: number
      cost: number
      color: string
      approved: boolean
    }[]
    approved: boolean
  }[]
}

interface TaskDetailList {
  type: 'active' | 'manual'
  service_name: string
  icon: string | null
  icon_color: string | null
  name: string | null
  file_path: string | null
  time: number
  cost: number
  label: {
    label_group_id: number
    label_group_name: string
    label_group_color: string
    label_id: number
    label_master_id: number
    label_name: string
    label_color: string
  } | null
}

const Detail: FC = () => {
  const { language } = useParams<{ language: LangValue }>()
  const location = useLocation()
  const { id } = useParams<{ id: string }>()
  const history = useHistory()
  const dispatch = useDispatch()

  const {
    task,
    login,
  } = useSelector((state: RootState) => state)

  const DISPLAY_UNDER_LIMIT = 6

  const DISPLAY_GRAPH_LIMIT = 4

  const DISPLAY_TASK_DETAIL_LIMIT_MINUTES = 1

  const [date, setDate] = useState<string>("")
  const [selectedLabelGroup, setSelectedLabelGroup] = useState<List>()
  const [isDisplayTime, setIsDisplayTime] = useState<boolean>(true)

  const [isDisplayAllDate, setIsDisplayAllDate] = useState<boolean>(true)
  const [isFirst, setIsFirst] = useState<boolean>(true)

  const [selectLabel, setSelectLabel] = useState<string>("")

  const [graphData, setGraphData] = useState<GraphData[]>([])

  const [graphDisplayRange, setGraphDisplayRange] = useState<{ startDate: string, endDate: string }>({ startDate: "", endDate: "" })

  const [isGraphLoading, setIsGraphLoading] = useState<boolean>(true)

  const [isDisplayDetailModal, setIsDisplayDetailModal] = useState<boolean>(false)
  const [isLoadingDetailModal, setIsLoadingDetailModal] = useState<boolean>(true)
  const [selectDetailMember, setSelectDetailMember] = useState<string>("")
  const [selectDetailLabel, setSelectDetailLabel] = useState<string>("")

  const [taskDetailList, setTaskDetailList] = useState<TaskDetailList[]>([])

  const [isDisplayLabelDetail, setIsDisplayLabelDetail] = useState<boolean>(false)

  const [isDisplayMemberDetail, setIsDisplayMemberDetail] = useState<boolean>(false)

  const [sortLabel, setSortLabel] = useState<string>("")

  const [displayType, setDisplayType] = useState<string>("all")

  const [displayExtraOrder, setDisplayExtraOrder] = useState<boolean>(false)

  const [isDisplayLabelCodeGroup, setIsDisplayLabelCodeGroup] = useState<boolean>(false)

  const endDate = useMemo(() => {
    if (Number(format(date, "YYYYMM")) === Number(nowDate("YYYYMM"))) {
      return nowDate("YYYY-MM-DD")
    }
    return endOfMonth(date, "YYYY-MM-DD")
  }, [date])

  const profit = useMemo(() => {
    if (!selectedLabelGroup) return 0
    return (selectedLabelGroup.cost.scheduled || 0)
    - (selectedLabelGroup.cost.others.reduce((s, v) => s + v.cost, 0) + selectedLabelGroup.cost.actual)
  }, [selectedLabelGroup])

  const graphLimit = useMemo(() => {
    if (!selectedLabelGroup) return 0
    if (profit > 0) {
      return selectedLabelGroup.cost.scheduled || 0
    }
    return (selectedLabelGroup.cost.others.reduce((s, v) => s + v.cost, 0) + selectedLabelGroup.cost.actual)
  }, [profit])

  const sortDate = () => {
    if (!selectedLabelGroup) return
    const [type, key, order] = sortLabel.split("@")
    if (!type || !key || !order) {
      setSelectedLabelGroup(task.taskReportLabelGroupDetail?.label_group)
      return
    }
    if (order === "asc") {
      if (type === "label") {
        const tmpSort = {
          ...selectedLabelGroup,
          labels: selectedLabelGroup.labels.map((v) => ({ ...v, members: sortBy(v.members || [], key) })),
        }
        setSelectedLabelGroup({
          ...selectedLabelGroup,
          labels: sortBy(tmpSort.labels || [], key),
        })
      } else if (type === "label_code") {
        const tmpSort = {
          ...selectedLabelGroup,
          label_codes: selectedLabelGroup.label_codes.map((v) => ({ ...v, labels: sortBy(v.labels || [], key) })),
        }
        setSelectedLabelGroup({
          ...selectedLabelGroup,
          label_codes: sortBy(tmpSort.label_codes || [], key),
        })
      } else {
        const tmpSort = {
          ...selectedLabelGroup,
          members: selectedLabelGroup.members.map((v) => ({ ...v, labels: sortBy(v.labels || [], key) })),
        }
        setSelectedLabelGroup({
          ...selectedLabelGroup,
          members: sortBy(tmpSort.members || [], key),
        })
      }
    } else if (order === "desc") {
      if (type === "label") {
        const tmpSort = {
          ...selectedLabelGroup,
          labels: selectedLabelGroup.labels.map((v) => ({ ...v, members: sortBy(v.members || [], key).reverse() })),
        }
        setSelectedLabelGroup({
          ...selectedLabelGroup,
          labels: sortBy(tmpSort.labels || [], key).reverse(),
        })
      } else if (type === "label_code") {
        const tmpSort = {
          ...selectedLabelGroup,
          label_codes: selectedLabelGroup.label_codes.map((v) => ({ ...v, labels: sortBy(v.labels || [], key).reverse() })),
        }
        setSelectedLabelGroup({
          ...selectedLabelGroup,
          label_codes: sortBy(tmpSort.label_codes || [], key).reverse(),
        })
      } else {
        const tmpSort = {
          ...selectedLabelGroup,
          members: selectedLabelGroup.members.map((v) => ({ ...v, labels: sortBy(v.labels || [], key).reverse() })),
        }
        setSelectedLabelGroup({
          ...selectedLabelGroup,
          members: sortBy(tmpSort.members || [], key).reverse(),
        })
      }
    }
  }

  const buildSortArea = (type: string, key: string) => {
    if (sortLabel === `${type}@${key}@asc`) {
      return (
        <>
          <FontAwesomeIcon
            icon={['fas', 'sort-numeric-down']}
            style={{ cursor: "pointer" }}
            onClick={() => setSortLabel(`${type}@${key}@desc`)}
          />
        </>
      )
    }
    if (sortLabel === `${type}@${key}@desc`) {
      return (
        <>
          <FontAwesomeIcon
            icon={['fas', 'sort-numeric-down-alt']}
            style={{ cursor: "pointer" }}
            onClick={() => setSortLabel("")}
          />
        </>
      )
    }
    return (
      <>
        <FontAwesomeIcon
          icon={['fas', 'sort']}
          style={{ cursor: "pointer" }}
          onClick={() => setSortLabel(`${type}@${key}@asc`)}
        />
      </>
    )
  }

  const clickGraph = (label: string) => {
    if (label === selectLabel) {
      setSelectLabel("")
      setIsDisplayAllDate(true)
      history.push(location.pathname)
      return
    }
    const tmpDate = format(label, "YYYY-MM-01")
    setDate(tmpDate)
    setIsDisplayAllDate(false)
    setSelectLabel(label)
    history.push(`${location.pathname}?date=${tmpDate}`)
  }

  const changeAccounting = (type: string, target_date: string, params: {budget?: number, other_costs?: { name: string, cost: number}[]}) => {
    const tmpParams:RequestPatchTaskReportLabelGroupDetailSummary = {
      label_group_id: id,
      target_date: target_date,
      accounting: {},
    }
    if (type === "budget" && tmpParams.accounting) {
      tmpParams.accounting.budget = params.budget || 0
    }
    if (type === "other_costs" && tmpParams.accounting) {
      tmpParams.accounting.other_costs = params.other_costs || []
    }
    dispatch(
      updateTaskReportLabelGroupDetailSummary(
        tmpParams,
        () => {
          if (target_date === startOfMonth(date, 'YYYY-MM-DD')) {
            dispatch(fetchTaskReportLabelGroupDetail(
              {
                id: id,
                start_date: startOfMonth(date, 'YYYY-MM-DD'),
                end_date: endDate,
              },
            ))
          }
          dispatch(fetchTaskReportLabelGroupDetailMax({ label_group_id: id }))
        },
      ),
    )
  }

  const changeScheduledTime = (time: number, target_date: string) => {
    const tmpParams:RequestPatchTaskReportLabelGroupDetailSummary = {
      label_group_id: id,
      target_date: target_date,
      labeling: {},
    }
    if (tmpParams.labeling) {
      tmpParams.labeling.scheduled = time || 0
    }
    dispatch(
      updateTaskReportLabelGroupDetailSummary(
        tmpParams,
        () => {
          if (target_date === startOfMonth(date, 'YYYY-MM-DD')) {
            dispatch(fetchTaskReportLabelGroupDetail(
              {
                id: id,
                start_date: startOfMonth(date, 'YYYY-MM-DD'),
                end_date: endDate,
              },
            ))
          }
          dispatch(fetchTaskReportLabelGroupDetailMax({ label_group_id: id }))
        },
      ),
    )
  }

  const selectGraphDisplayRange = (prev = false) => {
    if (!task.taskReportLabelGroupDetailSummary || !task.taskReportLabelGroupDetailSummary.months.length) return
    const tmpStartDate = dateManipulateMonth(graphDisplayRange.startDate, prev ? -1 : 1, "YYYY-MM-DD")
    const tmpEndDate = dateManipulateMonth(graphDisplayRange.endDate, prev ? -1 : 1, "YYYY-MM-DD")
    setGraphDisplayRange({
      startDate: tmpStartDate,
      endDate: tmpEndDate,
    })
    const currentList = task.taskReportLabelGroupDetailSummary.months.map((v) => v.date)
    if (!currentList.includes(prev ? tmpStartDate : tmpEndDate)) {
      setIsGraphLoading(true)
      dispatch(
        fetchTaskReportLabelGroupDetailSummary(
          {
            label_group_id: id,
            start_date: prev ? tmpStartDate : tmpEndDate,
            end_date: prev ? tmpStartDate : tmpEndDate,
          },
        ),
      )
    } else {
      const rangeList = [...Array(DISPLAY_GRAPH_LIMIT)].map(
        (_, i) => dateManipulateMonth(tmpStartDate, i, "YYYY-MM-DD"),
      )
      const dataList = task.taskReportLabelGroupDetailSummary?.months.filter((v) => rangeList.includes(v.date))
      setGraphData(dataList || [])
    }
  }

  const clickDetail = (memberId: number, labelId: number) => {
    if (login.function_enabled_state?.risk_assessment) {
      setIsLoadingDetailModal(true)
      const tmpReqValue: RequestGetTaskSearch = {
        member_ids: [memberId.toString()],
        label_master_ids: [labelId.toString()],
        label_group_ids: [id],
      }
      if (!isDisplayAllDate) {
        tmpReqValue.start_date = date
        tmpReqValue.end_date = endDate
      }
      dispatch(fetchTaskSearch(
        tmpReqValue,
      ))
      setSelectDetailMember(task.taskReportLabelGroupDetail?.label_group.members.find((v) => v.id === memberId)?.name || "")
      setSelectDetailLabel(task.taskReportLabelGroupDetail?.label_group.labels.find((v) => v.id === labelId)?.name || "")
    }
    setIsDisplayDetailModal(true)
  }

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    setSelectedLabelGroup(task.taskReportLabelGroupDetail?.label_group)
    setSortLabel("")
  }, [task.taskReportLabelGroupDetail])

  useEffect(() => {
    const getParams = getGetPrams(location.search) as GetParams
    if (getParams.date) {
      setDate(format(getParams.date, "YYYY-MM-DD"))
      setSelectLabel(format(getParams.date, "YYYY/MM"))
      setIsDisplayAllDate(false)
      return
    }
    setDate(startOfMonth(nowDate(), 'YYYY-MM-DD'))
  }, [])

  useEffect(() => {
    if (isDisplayAllDate) {
      setSelectLabel("")
      history.push(location.pathname)
      return
    }
    history.push(`${location.pathname}?date=${date}`)
  }, [isDisplayAllDate])

  useEffect(() => {
    if (isFirst) {
      setIsFirst(false)
      return
    }
    if (isDisplayAllDate) {
      setSelectLabel("")
      dispatch(fetchTaskReportLabelGroupDetail(
        {
          id: id,
        },
      ))
      return
    }
    setSelectLabel(startOfMonth(date, 'YYYY-MM-DD'))
    if (!task.taskReportLabelGroupDetailSummary?.months.map((v) => v.date).includes(startOfMonth(date, 'YYYY-MM-DD'))) {
      dispatch(resetTaskReportLabelGroupDetailSummary())
    }
    dispatch(fetchTaskReportLabelGroupDetail(
      {
        id: id,
        start_date: startOfMonth(date, 'YYYY-MM-DD'),
        end_date: endDate,
      },
    ))
  }, [date, isDisplayAllDate])

  useEffect(() => {
    dispatch(fetchTaskReportLabelGroupDetailMax({ label_group_id: id }))
    dispatch(resetTaskReportLabelGroupDetailSummary())
  }, [])

  useEffect(() => {
    if (!task.taskReportLabelGroupDetail) return
    if (task.taskReportLabelGroupDetailSummary?.months.length) return
    const targetDate = format(date || task.taskReportLabelGroupDetail.label_group.schedule.actual_end_date || nowDate(), "YYYY-MM-01")
    let tmpStartDate = ""
    let tmpEndDate = ""
    if (
      ((task.taskReportLabelGroupDetailSummary?.months.length || 0) < DISPLAY_GRAPH_LIMIT)
      || (
        Number(format(targetDate, "YYYYMM"))
        > Number(format(task.taskReportLabelGroupDetail.label_group.schedule.actual_end_date || nowDate(), "YYYYMM"))
      )
    ) {
      // フォーカスされている月がデータの終端を超えている場合
      tmpStartDate = dateManipulateMonth(date, -(DISPLAY_GRAPH_LIMIT - 1), "YYYY-MM-DD")
      tmpEndDate = format(date, "YYYY-MM-DD")
    } else if (Number(format(task.taskReportLabelGroupDetail.label_group.schedule.start_date, "YYYYMM"))
      > Number(format(targetDate, "YYYYMM"))) {
      // フォーカスされている月がデータの始端より前の場合
      tmpStartDate = format(date, "YYYY-MM-DD")
      tmpEndDate = dateManipulateMonth(date, (DISPLAY_GRAPH_LIMIT - 1), "YYYY-MM-DD")
    } else if (diffMonth(
      task.taskReportLabelGroupDetail.label_group.schedule.start_date,
      targetDate,
    ) < DISPLAY_GRAPH_LIMIT) {
      // 表示できるデータが表示上限数に収まっている場合
      tmpStartDate = format(task.taskReportLabelGroupDetail.label_group.schedule.start_date, "YYYY-MM-01")
      tmpEndDate = format(task.taskReportLabelGroupDetail.label_group.schedule.actual_end_date || nowDate(), "YYYY-MM-01")
    } else if (diffMonth(
      targetDate,
      format(task.taskReportLabelGroupDetail.label_group.schedule.actual_end_date || nowDate(), "YYYY-MM-01"),
    ) < DISPLAY_GRAPH_LIMIT) {
      // フォーカスされている月がデータの終端から数えて表示上限数内にある場合
      tmpStartDate = dateManipulateMonth(
        task.taskReportLabelGroupDetail.label_group.schedule.actual_end_date || nowDate(), -(DISPLAY_GRAPH_LIMIT - 1), "YYYY-MM-01",
      )
      tmpEndDate = format(task.taskReportLabelGroupDetail.label_group.schedule.actual_end_date || nowDate(), "YYYY-MM-01")
    } else if (diffMonth(format(task.taskReportLabelGroupDetail.label_group.schedule.start_date, "YYYY-MM-01"), targetDate) < DISPLAY_GRAPH_LIMIT) {
      // フォーカスされている月がデータの始端から数えて表示上限数内にある場合
      tmpStartDate = format(task.taskReportLabelGroupDetail.label_group.schedule.start_date, "YYYY-MM-01")
      tmpEndDate = dateManipulateMonth(task.taskReportLabelGroupDetail.label_group.schedule.start_date, (DISPLAY_GRAPH_LIMIT - 1), "YYYY-MM-01")
    } else if (DISPLAY_GRAPH_LIMIT % 2 === 0) {
      // フォーカスされている月がデータの始端・終端から表示上限数以上離れていて、表示上限数が偶数の場合
      tmpStartDate = dateManipulateMonth(targetDate, -((DISPLAY_GRAPH_LIMIT / 2) - 1), "YYYY-MM-01")
      tmpEndDate = dateManipulateMonth(targetDate, (DISPLAY_GRAPH_LIMIT / 2), "YYYY-MM-01")
    } else {
      // フォーカスされている月がデータの始端・終端から表示上限数以上離れていて、表示上限数が奇数の場合
      tmpStartDate = dateManipulateMonth(targetDate, -(Math.floor(DISPLAY_GRAPH_LIMIT / 2)), "YYYY-MM-01")
      tmpEndDate = dateManipulateMonth(targetDate, Math.floor(DISPLAY_GRAPH_LIMIT / 2), "YYYY-MM-01")
    }
    setIsGraphLoading(true)
    dispatch(
      fetchTaskReportLabelGroupDetailSummary(
        {
          label_group_id: id,
          start_date: tmpStartDate,
          end_date: tmpEndDate,
        },
      ),
    )
    setGraphDisplayRange({ startDate: tmpStartDate, endDate: tmpEndDate })
  }, [task.taskReportLabelGroupDetail])

  useEffect(() => {
    if (!task.taskReportLabelGroupDetailSummary?.months.length) return
    const rangeList = [...Array(DISPLAY_GRAPH_LIMIT)].map(
      (_, i) => dateManipulateMonth(graphDisplayRange.startDate, i, "YYYY-MM-DD"),
    )
    const dataList = task.taskReportLabelGroupDetailSummary?.months.filter((v) => rangeList.includes(v.date))
    setGraphData(dataList || [])
    setTimeout(() => {
      setIsGraphLoading(false)
    }, 500)
  }, [task.taskReportLabelGroupDetailSummary])

  useEffect(() => {
    setIsLoadingDetailModal(false)
    const buildTaskDetailList = task.taskSearch?.tasks.filter((v) => v.time >= (DISPLAY_TASK_DETAIL_LIMIT_MINUTES * 60)) || []
    const underLimitMinuteList = task.taskSearch?.tasks.filter((v) => v.time < (DISPLAY_TASK_DETAIL_LIMIT_MINUTES * 60)) || []
    if (underLimitMinuteList.length) {
      buildTaskDetailList.push({
        type: 'manual',
        service_name: "",
        icon: null,
        icon_color: null,
        name: `${DISPLAY_TASK_DETAIL_LIMIT_MINUTES}分以下の作業データが${underLimitMinuteList.length}件ありました`,
        file_path: null,
        time: underLimitMinuteList.reduce((s, v) => s + v.time, 0),
        cost: underLimitMinuteList.reduce((s, v) => s + v.cost, 0),
        label: null,
      })
    }
    setTaskDetailList(buildTaskDetailList)
  }, [task.taskSearch?.tasks])

  useEffect(() => {
    sortDate()
  }, [sortLabel])

  return (
    <section className="task_page">
      <Helmet>
        <title>Qasee - 工数管理 - プロジェクトレポート - 詳細</title>
      </Helmet>
      <section className="breadcrumb">
        工数管理
        &nbsp;&gt;&nbsp;
        <Link
          to={setPath(URL.TASK.path, language)}
        >
          プロジェクトレポート
        </Link>
        &nbsp;&gt;&nbsp;
        {selectedLabelGroup?.name}
        &nbsp;
      </section>
      <section className="page_header">
        <h1>
          {selectedLabelGroup?.name}
        </h1>
        <div className="tools">
          <button
            type="button"
            className={`display_switch ${isDisplayAllDate ? "check" : ""}`}
            onClick={() => setIsDisplayAllDate(!isDisplayAllDate)}
          >
            <div>
              <FontAwesomeIcon icon={['far', 'calendar-alt']} />
              <span>
                全期間
              </span>
            </div>
            <div>
              <FontAwesomeIcon icon={['far', 'calendar-check']} />
              <span>
                月別
              </span>
            </div>
          </button>
          <DateModule
            date={date}
            startMonth={format(task.taskReportLabelGroupDetail?.label_group.schedule.start_date, "YYYYMM")}
            changeCallback={(callback_date) => {
              setDate(callback_date)
              history.push(`${location.pathname}?date=${callback_date}`)
            }}
            disabled={isDisplayAllDate}
            noLimit
          />
        </div>
      </section>
      <section className="page_header">
        <div />
        <div className="tools">
          <PdfModule
            title={
              `Qaseeプロジェクトレポート ${selectedLabelGroup?.name} ${isDisplayAllDate ? "全期間" : format(date, "YYYY年MM月")}`
            }
            target="pdf_area"
            beforeFunc={() => {
              setIsDisplayMemberDetail(true)
              setIsDisplayLabelDetail(true)
            }}
            afterFunc={() => {
              setIsDisplayMemberDetail(false)
              setIsDisplayLabelDetail(false)
            }}
          />
        </div>
      </section>
      <section className="page_display_button_area">
        <div>
          <button
            type="button"
            className={displayType === "all" ? "on" : ""}
            onClick={() => setDisplayType("all")}
          >
            <FontAwesomeIcon icon={['far', 'file-alt']} style={{ fontSize: "20px" }} />
            すべて表示
          </button>
        </div>
        <div>
          <button
            type="button"
            className={displayType === "graph" ? "on" : ""}
            onClick={() => setDisplayType("graph")}
          >
            <FontAwesomeIcon icon={['fas', 'chart-bar']} style={{ fontSize: "20px" }} />
            分析グラフ
          </button>
          <button
            type="button"
            className={displayType === "label" ? "on" : ""}
            onClick={() => setDisplayType("label")}
          >
            <FontAwesomeIcon icon={['fas', 'list']} style={{ fontSize: "20px" }} />
            業務別実績
          </button>
          <button
            type="button"
            className={displayType === "member" ? "on" : ""}
            onClick={() => setDisplayType("member")}
          >
            <FontAwesomeIcon icon={['fas', 'users']} style={{ fontSize: "20px" }} />
            メンバー別実績
          </button>
          {
            login.permissions?.task_cost_view && (
              <button
                type="button"
                className={displayType === "cost" ? "on" : ""}
                onClick={() => setDisplayType("cost")}
              >
                <FontAwesomeIcon icon={['fas', 'coins']} style={{ fontSize: "20px" }} />
                損益計算
              </button>
            )
          }
        </div>
      </section>
      <section id="pdf_area">
        {
          ["all", "graph"].includes(displayType) && (
            <>
              <section className="page_contents content-area task_detail">
                <div className="content-block">
                  {
                    !!task.taskReportLabelGroupDetail?.threshold_time && (
                      <div className="block-header">
                        <div />
                        {`${format(task.taskReportLabelGroupDetail?.threshold_time, "YYYY年M月D日HH時mm分ss秒")}時点の集計データを表示しています`}
                      </div>
                    )
                  }
                  <div
                    className="block-detail"
                    style={{
                      position: "relative",
                    }}
                  >
                    <div className="tools" style={{ justifyContent: "space-between", marginBottom: "24px" }}>
                      {
                        login.permissions?.task_cost_view && (
                          <>
                            {
                              isDisplayTime ? (
                                <div />
                              ) : (
                                <div className="cost_viewable_label">
                                  コスト表示権限を持つユーザーのみ表示されています
                                </div>
                              )
                            }
                            <button
                              type="button"
                              className={`display_switch ${isDisplayTime ? "check" : ""}`}
                              onClick={() => setIsDisplayTime(!isDisplayTime)}
                            >
                              <div>
                                <FontAwesomeIcon icon={['far', 'clock']} />
                                <span>
                                  業務実績時間を表示
                                </span>
                              </div>
                              <div>
                                <FontAwesomeIcon icon={['fas', 'yen-sign']} />
                                <span>
                                  コストを表示
                                </span>
                              </div>
                            </button>
                          </>
                        )
                      }
                    </div>
                    <>
                      {
                        isDisplayTime ? (
                          <DetailScheduleChart
                            list={graphData.map((v) => (
                              {
                                label: v.date,
                                scheduled: v.labeling.scheduled,
                                values: v.labeling.labels.map((w) => (
                                  {
                                    name: w.name,
                                    value: w.time,
                                    color: w.color,
                                  }
                                )),
                                color: "#CCCCCC",
                              }
                            )) || []}
                            chartSize={300}
                            ruler
                            scale
                            type="hour"
                            vLabelLimit={5}
                            legend
                            tips
                            clickCallback={(label) => clickGraph(label)}
                            selectLabel={selectLabel}
                            max={task.taskReportLabelGroupDetailMax?.max_time || 0}
                            isGraphLoading={isGraphLoading}
                            round
                          />
                        ) : (
                          <DetailCostChart
                            list={graphData.map((v) => (
                              {
                                label: v.date,
                                sales: v.accounting.budget || 0,
                                member_cost: v.accounting.cost || 0,
                                other_cost: v.accounting.other_costs.reduce((s, w) => s + w.cost, 0) || 0,
                              }
                            )) || []}
                            chartSize={300}
                            ruler
                            scale
                            type="yen"
                            vLabelLimit={5}
                            tips
                            clickCallback={(label) => clickGraph(label)}
                            selectLabel={selectLabel}
                            max={task.taskReportLabelGroupDetailMax?.max_cost || 0}
                            isGraphLoading={isGraphLoading}
                          />
                        )
                      }
                      <div style={{ minHeight: "40px" }}>
                        <button
                          type="button"
                          onClick={() => selectGraphDisplayRange(true)}
                          style={{
                            position: "absolute",
                            bottom: isDisplayTime ? "210px" : "280px",
                            left: "100px",
                            transform: "translateY(5%)",
                            zIndex: "1",
                            fontSize: "20px",
                            color: "#999999",
                          }}
                        >
                          <FontAwesomeIcon icon={['fas', 'chevron-left']} />
                        </button>
                        <button
                          type="button"
                          onClick={() => selectGraphDisplayRange()}
                          style={{
                            position: "absolute",
                            bottom: isDisplayTime ? "210px" : "280px",
                            right: "-5px",
                            transform: "translateY(5%)",
                            zIndex: "1",
                            fontSize: "20px",
                            color: "#999999",
                          }}
                        >
                          <FontAwesomeIcon icon={['fas', 'chevron-right']} />
                        </button>
                      </div>
                      {
                        isDisplayTime && (
                          <>
                            <div
                              style={{
                                position: "relative",
                                marginLeft: "100px",
                                width: "calc(100% - 100px)",
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                padding: "0 16px",
                                justifyContent: "space-evenly",
                              }}
                            >
                              <div
                                style={{
                                  position: "absolute",
                                  top: 0,
                                  left: "-80px",
                                  padding: "16px",
                                  display: "flex",
                                  flexFlow: "column",
                                  gap: "22px",
                                  fontSize: "14px",
                                  textAlign: "right",
                                  color: "#4572ba",
                                  fontWeight: "bold",
                                }}
                              >
                                <div>
                                  予定時間
                                </div>
                              </div>
                              {
                                graphData.map((v) => (
                                  <DetailCostInputArea
                                    displayCount={6}
                                    scheduledTime={v.labeling.scheduled || 0}
                                    changeCallback={(value) => changeScheduledTime(value, v.date)}
                                    editable={false}
                                    key={`task_cost_area_${v.date}`}
                                  />
                                ))
                              }
                            </div>
                          </>
                        )
                      }
                      {
                        !isDisplayTime && login.permissions?.task_cost_view && (
                          <>
                            <div
                              style={{
                                position: "relative",
                                marginLeft: "100px",
                                width: "calc(100% - 100px)",
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                padding: "0 16px",
                                justifyContent: "space-evenly",
                              }}
                            >
                              <div
                                style={{
                                  position: "absolute",
                                  top: 0,
                                  left: "-80px",
                                  padding: "16px",
                                  display: "flex",
                                  flexFlow: "column",
                                  gap: "22px",
                                  fontSize: "14px",
                                  textAlign: "right",
                                  color: "#4572ba",
                                  fontWeight: "bold",
                                }}
                              >
                                <div>
                                  売上・予算
                                </div>
                                <div>
                                  その他のコスト
                                </div>
                                <div>
                                  業務コスト
                                </div>
                              </div>
                              {
                                graphData.map((v) => (
                                  <DetailScheduleInputArea
                                    displayCount={6}
                                    sales={v.accounting.budget || 0}
                                    member_cost={v.accounting.cost || 0}
                                    other_costs={v.accounting.other_costs?.map((w) => ({ name: w.name, cost: w.cost.toString() })) || []}
                                    changeCallback={(type, params) => changeAccounting(type, v.date, params)}
                                    editable={false}
                                    key={`task_cost_area_${v.date}`}
                                  />
                                ))
                              }
                            </div>
                          </>
                        )
                      }
                    </>
                  </div>
                </div>
              </section>
            </>
          )
        }
        {
          ["all", "label", "member", "cost"].includes(displayType) && (
            <>
              <section
                className="page_contents content-area task_detail"
                style={{
                  border: "solid 3px #4472ba",
                  borderRadius: "8px",
                }}
              >
                <div className="content-block">
                  {
                    task.isLoading ? (
                      <LoadingSquare>
                        <>
                          プロジェクト詳細を生成しています…
                        </>
                      </LoadingSquare>
                    ) : (
                      <>
                        {
                          selectedLabelGroup ? (
                            <>
                              <div
                                style={{
                                  width: "calc(100% + 96px)",
                                  backgroundColor: "#4472ba",
                                  color: "#FFFFFF",
                                  fontSize: "18px",
                                  margin: "-48px -16px 40px -48px",
                                  padding: "24px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  gap: "8px",
                                  fontWeight: "bold",
                                }}
                              >
                                表示データ：
                                <div
                                  style={{
                                    backgroundColor: "#FFFFFF",
                                    color: "#4572ba",
                                    borderRadius: "4px",
                                    padding: "8px 24px",
                                    fontSize: "24px",
                                    display: "flex",
                                    alignItems: "flex-end",
                                    gap: "4px",
                                  }}
                                >
                                  {
                                    isDisplayAllDate ? (
                                      <>
                                        <div>
                                          <span
                                            style={{
                                              fontSize: "18px",
                                              marginRight: "4px",
                                            }}
                                          >
                                            {format(selectedLabelGroup.schedule.start_date, "YYYY年")}
                                          </span>
                                          {format(selectedLabelGroup.schedule.start_date, "MM月DD日")}
                                        </div>
                                        ~
                                        {
                                          selectedLabelGroup.schedule.actual_end_date && (
                                            <div>
                                              <span
                                                style={{
                                                  fontSize: "18px",
                                                  marginRight: "4px",
                                                }}
                                              >
                                                {format(selectedLabelGroup.schedule.actual_end_date, "YYYY年")}
                                              </span>
                                              {format(selectedLabelGroup.schedule.actual_end_date, "MM月DD日")}
                                            </div>
                                          )
                                        }
                                        {
                                          !selectedLabelGroup.schedule.actual_end_date && selectedLabelGroup.schedule.scheduled_end_date && (
                                            <div>
                                              <span
                                                style={{
                                                  fontSize: "18px",
                                                  marginRight: "4px",
                                                }}
                                              >
                                                {format(selectedLabelGroup.schedule.scheduled_end_date, "YYYY年")}
                                              </span>
                                              {format(selectedLabelGroup.schedule.scheduled_end_date, "MM月DD日")}
                                              終了予定
                                            </div>
                                          )
                                        }
                                      </>
                                    ) : (
                                      <>
                                        <div>
                                          <span
                                            style={{
                                              fontSize: "18px",
                                              marginRight: "4px",
                                            }}
                                          >
                                            {format(date, "YYYY年")}
                                          </span>
                                          {startOfMonth(date, "MM月DD日")}
                                        </div>
                                        ~
                                        <div>
                                          <span
                                            style={{
                                              fontSize: "18px",
                                              marginRight: "4px",
                                            }}
                                          >
                                            {format(endDate, "YYYY年")}
                                          </span>
                                          {format(endDate, "MM月DD日")}
                                        </div>
                                      </>
                                    )
                                  }
                                </div>
                              </div>
                              <div className="block-detail">
                                {
                                  !!task.taskReportLabelGroupDetail?.threshold_time && (
                                    <div
                                      style={{
                                        textAlign: "right",
                                        marginBottom: "16px",
                                      }}
                                    >
                                      {`${format(task.taskReportLabelGroupDetail?.threshold_time, "YYYY年M月D日HH時mm分ss秒")}時点の集計データを表示しています`}
                                    </div>
                                  )
                                }
                                {
                                  ["all", "label"].includes(displayType) && (
                                    <>
                                      <div className="box_area">
                                        <div className="title_area">
                                          <div className="title">
                                            業務別実績情報
                                          </div>
                                        </div>
                                        <ReportSummaryModule
                                          list={selectedLabelGroup.labels || []}
                                          type="label"
                                          graphTitle="業務別実績情報"
                                          headerTitle="業務(実績時間が多い順)"
                                          chartSize={300}
                                        />
                                        {
                                          isDisplayLabelDetail ? (
                                            <>
                                              {
                                                selectedLabelGroup.members.length ? (
                                                  <>
                                                    <div className="list_row_wrapper">
                                                      <div className={`list_row header_area${login.permissions?.task_cost_view ? " cost_view" : ""}`}>
                                                        <div className="title_display_area">
                                                          業務名
                                                        </div>
                                                        <div className="work_time">
                                                          業務時間
                                                          {buildSortArea("label", "time")}
                                                        </div>
                                                        {
                                                          login.permissions?.task_cost_view && (
                                                            <div className="work_cost">
                                                              業務コスト
                                                              {buildSortArea("label", "cost")}
                                                              <Tips
                                                                description="コスト表示権限を持つユーザーのみ表示されています"
                                                                className="fit"
                                                                icon={false}
                                                              >
                                                                <>
                                                                  <FontAwesomeIcon icon={['far', 'question-circle']} />
                                                                </>
                                                              </Tips>
                                                            </div>
                                                          )
                                                        }
                                                      </div>
                                                    </div>
                                                    <div>
                                                      {
                                                        selectedLabelGroup.labels.map((label) => (
                                                          <DetailRow
                                                            key={`detail_row_${id}_${label.name}_${label.id}`}
                                                            list={{
                                                              ...label,
                                                              details: label.members,
                                                            }}
                                                            type="label"
                                                            costView={login.permissions?.task_cost_view || false}
                                                            clickDetailCallback={(memberId, labelId) => clickDetail(memberId, labelId)}
                                                            // eslint-disable-next-line max-len
                                                            displayApprovalIcon={isDisplayAllDate ? Number(format(selectedLabelGroup.schedule.start_date, "YYYYMMDD")) > 20240430 : Number(format(date, "YYYYMMDD")) > 20240430}
                                                            round
                                                          />
                                                        ))
                                                      }
                                                    </div>
                                                  </>
                                                ) : (
                                                  <div
                                                    style={{
                                                      marginBottom: "24px",
                                                      fontSize: "14px",
                                                      textAlign: "center",
                                                    }}
                                                  >
                                                    データがありません
                                                  </div>
                                                )
                                              }
                                            </>
                                          ) : (
                                            <></>
                                          )
                                        }
                                        <div
                                          onClick={() => setIsDisplayLabelDetail(!isDisplayLabelDetail)}
                                          role="none"
                                          style={{
                                            marginTop: "24px",
                                            textAlign: "center",
                                            backgroundColor: "#4572ba",
                                            color: "#FFFFFF",
                                            padding: "8px",
                                            borderRadius: "4px",
                                            cursor: "pointer",
                                            fontWeight: "bold",
                                            fontSize: "16px",
                                          }}
                                        >
                                          {isDisplayLabelDetail ? "詳細一覧を非表示にする" : "詳細一覧を表示する"}
                                        </div>
                                      </div>
                                      {
                                        !!selectedLabelGroup.label_codes.length && (
                                          <div className="box_area">
                                            <div className="title_area">
                                              <div className="title">
                                                業務コード別実績情報
                                              </div>
                                            </div>
                                            <ReportSummaryModule
                                              list={
                                                selectedLabelGroup.label_codes?.map((v, i) => ({
                                                  id: i + 1,
                                                  name: v.code || "業務コードなし",
                                                  time: v.time,
                                                  cost: v.cost,
                                                  color: v.code ? stringToHexSimple(v.code) : "#CCCCCC",
                                                })) || []
                                              }
                                              type="label"
                                              graphTitle="業務コード別実績情報"
                                              headerTitle="業務コード(実績時間が多い順)"
                                              chartSize={300}
                                            />
                                            {
                                              isDisplayLabelCodeGroup ? (
                                                <>
                                                  {
                                                    selectedLabelGroup.members.length ? (
                                                      <>
                                                        <div className="list_row_wrapper">
                                                          { /* eslint-disable-next-line max-len */ }
                                                          <div className={`list_row header_area${login.permissions?.task_cost_view ? " cost_view" : ""}`}>
                                                            <div className="title_display_area">
                                                              業務コード名
                                                            </div>
                                                            <div className="work_time">
                                                              業務時間
                                                              {buildSortArea("label_code", "time")}
                                                            </div>
                                                            {
                                                              login.permissions?.task_cost_view && (
                                                                <div className="work_cost">
                                                                  業務コスト
                                                                  {buildSortArea("label_code", "cost")}
                                                                  <Tips
                                                                    description="コスト表示権限を持つユーザーのみ表示されています"
                                                                    className="fit"
                                                                    icon={false}
                                                                  >
                                                                    <>
                                                                      <FontAwesomeIcon icon={['far', 'question-circle']} />
                                                                    </>
                                                                  </Tips>
                                                                </div>
                                                              )
                                                            }
                                                          </div>
                                                        </div>
                                                        <div>
                                                          {
                                                            selectedLabelGroup.label_codes?.map((label_code, i) => (
                                                              <DetailRow
                                                                key={`detail_row_${id}_${label_code.code}`}
                                                                list={{
                                                                  ...label_code,
                                                                  id: i + 1,
                                                                  name: label_code.code || "業務コードなし",
                                                                  color: label_code.code ? stringToHexSimple(label_code.code) : "#CCCCCC",
                                                                  details: label_code.labels,
                                                                }}
                                                                type="label_code"
                                                                costView={login.permissions?.task_cost_view || false}
                                                                clickDetailCallback={(memberId, labelId) => clickDetail(memberId, labelId)}
                                                                // eslint-disable-next-line max-len
                                                                displayApprovalIcon={isDisplayAllDate ? Number(format(selectedLabelGroup.schedule.start_date, "YYYYMMDD")) > 20240430 : Number(format(date, "YYYYMMDD")) > 20240430}
                                                                round
                                                              />
                                                            ))
                                                          }
                                                        </div>
                                                      </>
                                                    ) : (
                                                      <div
                                                        style={{
                                                          marginBottom: "24px",
                                                          fontSize: "14px",
                                                          textAlign: "center",
                                                        }}
                                                      >
                                                        データがありません
                                                      </div>
                                                    )
                                                  }
                                                </>
                                              ) : (
                                                <></>
                                              )
                                            }
                                            <div
                                              onClick={() => setIsDisplayLabelCodeGroup(!isDisplayLabelCodeGroup)}
                                              role="none"
                                              style={{
                                                marginTop: "24px",
                                                textAlign: "center",
                                                backgroundColor: "#4572ba",
                                                color: "#FFFFFF",
                                                padding: "8px",
                                                borderRadius: "4px",
                                                cursor: "pointer",
                                                fontWeight: "bold",
                                                fontSize: "16px",
                                              }}
                                            >
                                              {isDisplayLabelCodeGroup ? "詳細一覧を非表示にする" : "詳細一覧を表示する"}
                                            </div>
                                          </div>
                                        )
                                      }
                                    </>
                                  )
                                }
                                {
                                  ["all", "member"].includes(displayType) && (
                                    <div className="box_area">
                                      <div className="title_area">
                                        <div className="title">
                                          メンバー別実績情報
                                        </div>
                                      </div>
                                      <ReportSummaryModule
                                        list={selectedLabelGroup.members}
                                        type="member"
                                        graphTitle="メンバー別実績情報"
                                        headerTitle="メンバー(実績時間が多い順)"
                                        chartSize={300}
                                      />
                                      {
                                        isDisplayMemberDetail ? (
                                          <>
                                            {
                                              selectedLabelGroup.members.length ? (
                                                <>
                                                  <div className="list_row_wrapper">
                                                    <div className={`list_row header_area${login.permissions?.task_cost_view ? " cost_view" : ""}`}>
                                                      <div className="title_display_area">
                                                        メンバー名
                                                      </div>
                                                      <div className="work_time">
                                                        業務時間
                                                        {buildSortArea("member", "time")}
                                                      </div>
                                                      {
                                                        login.permissions?.task_cost_view && (
                                                          <div className="work_cost">
                                                            業務コスト
                                                            {buildSortArea("member", "cost")}
                                                            <Tips
                                                              description="コスト表示権限を持つユーザーのみ表示されています"
                                                              className="fit"
                                                              icon={false}
                                                            >
                                                              <>
                                                                <FontAwesomeIcon icon={['far', 'question-circle']} />
                                                              </>
                                                            </Tips>
                                                          </div>
                                                        )
                                                      }
                                                    </div>
                                                  </div>
                                                  <div>
                                                    {
                                                      selectedLabelGroup.members.map((member) => (
                                                        <DetailRow
                                                          key={`detail_row_${id}_${member.name}_${member.id}`}
                                                          list={{
                                                            ...member,
                                                            details: member.labels,
                                                          }}
                                                          type="member"
                                                          costView={login.permissions?.task_cost_view || false}
                                                          clickDetailCallback={(memberId, labelId) => clickDetail(memberId, labelId)}
                                                          // eslint-disable-next-line max-len
                                                          displayApprovalIcon={isDisplayAllDate ? Number(format(selectedLabelGroup.schedule.start_date, "YYYYMMDD")) > 20240430 : Number(format(date, "YYYYMMDD")) > 20240430}
                                                          round
                                                        />
                                                      ))
                                                    }
                                                  </div>
                                                </>
                                              ) : (
                                                <div
                                                  style={{
                                                    marginBottom: "24px",
                                                    fontSize: "14px",
                                                    textAlign: "center",
                                                  }}
                                                >
                                                  データがありません
                                                </div>
                                              )
                                            }
                                          </>
                                        ) : (
                                          <></>
                                        )
                                      }
                                      <div
                                        onClick={() => setIsDisplayMemberDetail(!isDisplayMemberDetail)}
                                        role="none"
                                        style={{
                                          marginTop: "24px",
                                          textAlign: "center",
                                          backgroundColor: "#4572ba",
                                          color: "#FFFFFF",
                                          padding: "8px",
                                          borderRadius: "4px",
                                          cursor: "pointer",
                                          fontWeight: "bold",
                                          fontSize: "16px",
                                        }}
                                      >
                                        {isDisplayMemberDetail ? "詳細一覧を非表示にする" : "詳細一覧を表示する"}
                                      </div>
                                    </div>
                                  )
                                }
                                {
                                  login.permissions?.task_cost_view && ["all", "cost"].includes(displayType) ? (
                                    <div className="box_area">
                                      <div className="title_area">
                                        <div className="title">
                                          損益計算
                                          <span className="cost_view">
                                            コスト表示権限を持つユーザーのみ表示されています
                                          </span>
                                        </div>
                                      </div>
                                      <div className="profit_area">
                                        <div className="breakdown_list">
                                          <div className="breakdown_row budget">
                                            <div>
                                              売上・予算
                                            </div>
                                            <div>
                                              {`¥${Math.floor(selectedLabelGroup.cost.scheduled || 0).toLocaleString() || "0"}`}
                                            </div>
                                          </div>
                                          <div className="breakdown_row work_cost">
                                            <div>
                                              業務コスト
                                            </div>
                                            <div>
                                              {`¥${Math.floor(selectedLabelGroup.cost.actual).toLocaleString() || "0"}`}
                                            </div>
                                          </div>
                                          <div>
                                            {
                                              selectedLabelGroup.labels.map((label) => (
                                                <div
                                                  key={uniqueId()}
                                                  className="breakdown_row work_cost_detail"
                                                >
                                                  <div>
                                                    <div
                                                      className="icon"
                                                      style={{
                                                        backgroundColor: label.color,
                                                      }}
                                                    />
                                                    {label.name}
                                                  </div>
                                                  <div>
                                                    {`¥${label.cost.toLocaleString()}`}
                                                  </div>
                                                </div>
                                              ))
                                            }
                                          </div>
                                          <div className="breakdown_row other_cost">
                                            <div>
                                              その他のコスト
                                            </div>
                                            <div>
                                              {`¥${selectedLabelGroup.cost.others.reduce((s, v) => s + v.cost, 0).toLocaleString()}`}
                                            </div>
                                          </div>
                                          <div>
                                            {
                                              selectedLabelGroup.cost.others.map((other) => (
                                                <div
                                                  key={uniqueId()}
                                                  className="breakdown_row other_cost_detail"
                                                >
                                                  <div>
                                                    {other.name}
                                                  </div>
                                                  <div>
                                                    {`¥${other.cost.toLocaleString()}`}
                                                  </div>
                                                </div>
                                              ))
                                            }
                                          </div>
                                          <div className={`breakdown_row result${profit <= 0 ? " negative" : ""}`}>
                                            <div>
                                              {profit <= 0 ? "損失" : "利益"}
                                            </div>
                                            <div>
                                              {`¥${Math.floor(Math.abs(profit)).toLocaleString()}`}
                                            </div>
                                          </div>
                                        </div>
                                        <div className="graph_wrapper">
                                          <div className="graph_area">
                                            <div>
                                              {
                                                selectedLabelGroup.labels.map((label) => (
                                                  <React.Fragment key={uniqueId()}>
                                                    {
                                                      label.cost ? (
                                                        <div
                                                          className="block work"
                                                          style={{
                                                            height: `${(label.cost / graphLimit) * 100}%`,
                                                          }}
                                                        >
                                                          {
                                                            ((label.cost / graphLimit) * 100 > DISPLAY_UNDER_LIMIT) ? (
                                                              <div className="text_area">
                                                                <div>
                                                                  <div
                                                                    className="icon"
                                                                    style={{
                                                                      backgroundColor: label.color,
                                                                    }}
                                                                  />
                                                                  {label.name}
                                                                </div>
                                                                <div>
                                                                  {`¥${label.cost.toLocaleString()}`}
                                                                </div>
                                                              </div>
                                                            ) : (
                                                              <Tips
                                                                description={`${label.name}:¥${label.cost.toLocaleString()}`}
                                                                className="min"
                                                                icon={false}
                                                              >
                                                                <>
                                                                </>
                                                              </Tips>
                                                            )
                                                          }
                                                        </div>
                                                      ) : (
                                                        <></>
                                                      )
                                                    }
                                                  </React.Fragment>
                                                ))
                                              }
                                              {
                                                selectedLabelGroup.cost.others.map((other) => (
                                                  <React.Fragment key={uniqueId()}>
                                                    <div
                                                      className="block other"
                                                      style={{
                                                        height: `${(other.cost / graphLimit) * 100}%`,
                                                      }}
                                                    >
                                                      {
                                                        ((other.cost / graphLimit) * 100 > DISPLAY_UNDER_LIMIT) ? (
                                                          <div className="text_area">
                                                            <div>
                                                              {other.name}
                                                            </div>
                                                            <div>
                                                              {`¥${Math.floor(other.cost).toLocaleString()}`}
                                                            </div>
                                                          </div>
                                                        ) : (
                                                          <Tips
                                                            description={`${other.name}:¥${Math.floor(other.cost).toLocaleString()}`}
                                                            className="min"
                                                            icon={false}
                                                          >
                                                            <>
                                                            </>
                                                          </Tips>
                                                        )
                                                      }
                                                    </div>
                                                  </React.Fragment>
                                                ))
                                              }
                                              {
                                                profit > 0 && (
                                                  <div
                                                    className="block result"
                                                    style={{
                                                      height: `${(profit / graphLimit) * 100}%`,
                                                    }}
                                                  >
                                                    {
                                                      ((profit / graphLimit) * 100 > DISPLAY_UNDER_LIMIT) ? (
                                                        <div className="text_area">
                                                          <div>
                                                            利益
                                                          </div>
                                                          <div>
                                                            {`¥${Math.floor(profit).toLocaleString()}`}
                                                          </div>
                                                        </div>
                                                      ) : (
                                                        <Tips
                                                          description={`利益:¥${Math.floor(profit).toLocaleString()}`}
                                                          className="min"
                                                          icon={false}
                                                        >
                                                          <>
                                                          </>
                                                        </Tips>
                                                      )
                                                    }
                                                  </div>
                                                )
                                              }
                                            </div>
                                            <div>
                                              {
                                                selectedLabelGroup.cost.scheduled && selectedLabelGroup.cost.scheduled > 0 ? (
                                                  <div
                                                    className="block budget"
                                                    style={{
                                                      height: `${(selectedLabelGroup.cost.scheduled / graphLimit) * 100}%`,
                                                    }}
                                                  >
                                                    {
                                                      ((selectedLabelGroup.cost.scheduled / graphLimit) * 100 > DISPLAY_UNDER_LIMIT) ? (
                                                        <div className="text_area">
                                                          <div>
                                                            売上・予算
                                                          </div>
                                                          <div>
                                                            {`¥${Math.floor(selectedLabelGroup.cost.scheduled).toLocaleString()}`}
                                                          </div>
                                                        </div>
                                                      ) : (
                                                        <Tips
                                                          description={`売上・予算:¥${Math.floor(selectedLabelGroup.cost.scheduled).toLocaleString()}`}
                                                          className="min"
                                                          icon={false}
                                                        >
                                                          <>
                                                          </>
                                                        </Tips>
                                                      )
                                                    }
                                                  </div>
                                                ) : (
                                                  <></>
                                                )
                                              }
                                              {
                                                profit <= 0 && (
                                                  <div
                                                    className="block result negative"
                                                    style={{
                                                      height: `${(Math.abs(profit) / graphLimit) * 100}%`,
                                                    }}
                                                  >
                                                    {
                                                      ((Math.abs(profit) / graphLimit) * 100 > DISPLAY_UNDER_LIMIT) ? (
                                                        <div className="text_area">
                                                          <div>
                                                            損失
                                                          </div>
                                                          <div>
                                                            {`¥${Math.floor(Math.abs(profit)).toLocaleString()}`}
                                                          </div>
                                                        </div>
                                                      ) : (
                                                        <Tips
                                                          description={`損失:¥${Math.floor(Math.abs(profit)).toLocaleString()}`}
                                                          className="min"
                                                          icon={false}
                                                        >
                                                          <>
                                                          </>
                                                        </Tips>
                                                      )
                                                    }
                                                  </div>
                                                )
                                              }
                                            </div>
                                          </div>
                                          <div className="label_area">
                                            <div>
                                              コストと利益
                                            </div>
                                            <div>
                                              収益
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  ) : (
                                    <></>
                                  )
                                }
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="block-detail">
                                <div
                                  style={{
                                    textAlign: "center",
                                  }}
                                >
                                  <div
                                    style={{
                                      marginBottom: "24px",
                                      fontSize: "14px",
                                    }}
                                  >
                                    該当するプロジェクトがありませんでした
                                  </div>
                                </div>
                              </div>
                            </>
                          )
                        }
                      </>
                    )
                  }
                </div>
              </section>
            </>
          )
        }
      </section>
      <Modal
        show={isDisplayDetailModal}
        wrapperClass={login.function_enabled_state?.risk_assessment ? "label_detail_modal" : "promotionModal"}
        closeCallback={() => setIsDisplayDetailModal(false)}
        closeable
      >
        {
          login.function_enabled_state?.risk_assessment ? (
            <>
              {
                isLoadingDetailModal ? (
                  <>
                    <LoadingSquare>
                      <>
                        プロジェクト詳細を生成しています…
                      </>
                    </LoadingSquare>
                  </>
                ) : (
                  <>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-around",
                        marginBottom: "24px",
                        fontSize: "20px",
                        fontWeight: "bold",
                        color: "#4572BA",
                      }}
                    >
                      {`${selectDetailMember}さん ${selectDetailLabel}の詳細`}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-around",
                        marginBottom: "24px",
                        fontSize: "20px",
                        fontWeight: "bold",
                        color: "#4572BA",
                      }}
                    >
                      <div>
                        {secToHour(task.taskSearch?.tasks.reduce((s, v) => (s + v.time), 0) || 0)}
                        時間
                        {secToTwoDigitsMinute(task.taskSearch?.tasks.reduce((s, v) => (s + v.time), 0) || 0)}
                        分
                      </div>
                      {
                        login.permissions?.task_cost_view && (
                          <div>
                            {`¥${Math.round(task.taskSearch?.tasks.reduce((s, v) => (s + v.cost), 0) || 0).toLocaleString()}`}
                          </div>
                        )
                      }
                    </div>
                    <table
                      style={{
                        display: "flex",
                        flexFlow: "column",
                      }}
                    >
                      <thead>
                        <tr
                          style={{
                            display: "flex",
                            borderBottom: "solid 1px #999999",
                          }}
                        >
                          <th
                            style={{
                              padding: "8px",
                              textAlign: "center",
                              width: "70%",
                            }}
                          >
                            作業データ
                          </th>
                          <th
                            style={{
                              padding: "8px",
                              textAlign: "center",
                              width: login.permissions?.task_cost_view ? "15%" : "30%",
                            }}
                          >
                            時間
                          </th>
                          {
                            login.permissions?.task_cost_view && (
                              <th
                                style={{
                                  padding: "8px",
                                  textAlign: "center",
                                  width: "15%",
                                }}
                              >
                                コスト
                              </th>
                            )
                          }
                        </tr>
                      </thead>
                      <tbody
                        style={{
                          maxHeight: "300px",
                          overflowY: "scroll",
                        }}
                      >
                        {
                          taskDetailList.map((taskItem) => (
                            <tr
                              key={taskItem.name}
                              style={{
                                display: "flex",
                              }}
                            >
                              <td
                                style={{
                                  padding: "8px",
                                  wordBreak: "break-all",
                                  width: "70%",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "8px",
                                  }}
                                >
                                  <div
                                    className="icon_area"
                                    style={{
                                      color: taskItem.icon_color || "#000000",
                                    }}
                                  >
                                    {
                                      taskItem.icon ? (
                                        <FontAwesomeIcon
                                          icon={[taskItem.icon.split(" ")[0] as IconPrefix, taskItem.icon.split(" ")[1] as IconName]}
                                          fixedWidth
                                        />
                                      ) : (
                                        <FontAwesomeIcon
                                          icon={taskItem.name ? ["fas", "check-circle"] : ["fas", "exclamation-triangle"]}
                                          fixedWidth
                                        />
                                      )
                                    }
                                  </div>
                                  {taskItem.name}
                                </div>
                              </td>
                              <td
                                style={{
                                  padding: "8px",
                                  textAlign: "right",
                                  wordBreak: "break-all",
                                  width: login.permissions?.task_cost_view ? "15%" : "30%",
                                }}
                              >
                                <div>
                                  {`${secToHour(taskItem.time)}時間${secToTwoDigitsMinute(taskItem.time)}分`}
                                </div>
                              </td>
                              {
                                login.permissions?.task_cost_view && (
                                  <td
                                    style={{
                                      padding: "8px",
                                      textAlign: "right",
                                      wordBreak: "break-all",
                                      width: "15%",
                                    }}
                                  >
                                    <div>
                                      {`¥${Math.round(taskItem.cost || 0).toLocaleString()}`}
                                    </div>
                                  </td>
                                )
                              }
                            </tr>
                          ))
                        }
                      </tbody>
                    </table>
                  </>
                )
              }
            </>
          ) : (
            <>
              <div className="header_text">
                ご利用になるにはリスクアセスメント機能の契約が必要です
              </div>
              <div className="title">
                業務詳細表示
              </div>
              <div style={{ marginBottom: "24px" }}>
                <p>
                  業務のさらに詳細な内訳を表示することができます。
                </p>
                <p>
                  業務の分析やプロセスの改善などに役立てることができます。
                </p>
                <div>
                  <img
                    src="/static/images/promotion/image_task_search.png"
                    alt="業務詳細表示"
                    style={{
                      width: "100%",
                      border: "solid 1px #CCCCCC",
                      borderRadius: "10px",
                    }}
                  />
                  <div
                    style={{
                      fontSize: "120px",
                      fontWeight: "bold",
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translateX(-50%) translateY(-25%) rotate(-20deg)",
                      color: "rgb(255 121 121 / 30%)",
                    }}
                  >
                    SAMPLE
                  </div>
                </div>
              </div>
              <div className="footer_text">
                {
                  (isAdministrator(login.role) && login.change_contract) ? (
                    <button
                      type="button"
                      className="button change_contract"
                      onClick={() => {
                        setDisplayExtraOrder(true)
                        setIsDisplayDetailModal(false)
                      }}
                    >
                      <FontAwesomeIcon icon={['far', 'lightbulb']} />
                      オプション機能の開放
                    </button>
                  ) : (
                    <>
                      機能解放に関するご相談は管理者またはカスタマーサクセスまでご連絡ください
                    </>
                  )
                }
              </div>
            </>
          )
        }
      </Modal>
      <ExtraOrderModal
        display={displayExtraOrder}
        closeCallback={() => setDisplayExtraOrder(false)}
      />
    </section>
  )
}

export default Detail
